import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import EventImage from "../../assets/event-image1.jpeg";
import axios from "axios";
import { analytics, logEvent } from "../../config/firebaseconfig";
import { constants } from "../../constants";
import logo from "../../assets/mslogo1.png";
import { ChevronLeft, Clock, Gift, Users2, Target, CheckCircle2, Star, MapPin } from "lucide-react";

export default function EventDetailPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [submitLoading, setSubmitLoading] = useState(false);

  const source = searchParams.get("source") || "";
  const sourceRemark = searchParams.get("source_remark") || "";
  const branch = searchParams.get("branch") || "1";

  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(null);
  const [activeLocation, setActiveLocation] = useState(0);
  const [educationOptions, setEducationOptions] = useState([]);
  const [examOptions, setExamOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [error, setError] = useState(null);

  const formatDateRange = (locations) => {
    if (!locations || locations.length === 0) return "";
    const sortedLocations = [...locations].sort((a, b) => new Date(a.date) - new Date(b.date));
    const firstDate = new Date(sortedLocations[0].date);
    const lastDate = new Date(sortedLocations[sortedLocations.length - 1].date);
    const options = { month: "long", day: "numeric", year: "numeric" };
    if (firstDate.getTime() === lastDate.getTime()) return firstDate.toLocaleDateString("en-US", options);
    return `${firstDate.toLocaleDateString("en-US", options)} - ${lastDate.toLocaleDateString("en-US", options)}`;
  };
  
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    mobile_no: "",
    email_id: "",
    educationlevel_id: "",
    exam_id: "",
    country_id: "",
    venue: "",
    source: source,
    source_remark: sourceRemark,
    branch: branch,
  });

  useEffect(() => {
    const handleInteraction = () => {
      logEvent(analytics, "events_page_visited", {
        timestamp: new Date().toISOString(),
      });

      // Remove listener after logging to avoid multiple triggers
      window.removeEventListener("scroll", handleInteraction);
      window.removeEventListener("click", handleInteraction);
    };

    // Add listeners for interaction
    window.addEventListener("scroll", handleInteraction);
    window.addEventListener("click", handleInteraction);

    return () => {
      window.removeEventListener("scroll", handleInteraction);
      window.removeEventListener("click", handleInteraction);
    };
  }, []);

  useEffect(() => {
    // Fetch event details using the ID
    const fetchEvent = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch(`${constants.serverURL}/api/events/getEvent/${id}`);
        
        
        const data = await response.json();
        
        if (data.status != "success") {
          throw new Error('Event not found');
        }
        if (data && data.event) {
          setEvent(data.event);
          
          // Set the first location as active by default
          if (data.event.locations && data.event.locations.length > 0) {
            setActiveLocation(0);
          }
        } else {
          throw new Error('Event data not found');
        }
        
      } catch (error) {
        console.error("Error fetching event:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    const fetchEducationOptions = () => {
      // Hardcoded options as requested, mapping to existing IDs where available
      const options = [
        { educationlevel_id: "2", educationlevel: "12TH" },
        { educationlevel_id: "5", educationlevel: "DIPLOMA" },
        { educationlevel_id: "3", educationlevel: "BACHELORS" },
        { educationlevel_id: "4", educationlevel: "MASTERS" },
        { educationlevel_id: "PHD", educationlevel: "PHD" },
        { educationlevel_id: "OTHERS", educationlevel: "OTHERS" },
      ];
      setEducationOptions(options);
    };

    const fetchExamOptions = () => {
      axios
        .get("https://crmportal.maheshsuchit.com/teqcoder-api/english-exam.php")
        .then((response) => {
          if (response.data && response.data.data) {
            setExamOptions(response.data.data);
          }
        })
        .catch((error) => {
          console.error("Error fetching exam options:", error);
        });
    };

    const fetchCountries = () => {
      // Hardcoded options as requested, mapping to existing IDs where available
      const options = [
        { country_id: "4", country: "USA" },
        { country_id: "3", country: "CANADA" },
        { country_id: "5", country: "UK" },
        { country_id: "EUROPE", country: "EUROPE" },
        { country_id: "18", country: "GERMANY" },
        { country_id: "DUBAI", country: "DUBAI" },
        { country_id: "OTHERS", country: "OTHERS" },
      ];
      setCountryOptions(options);
    };

    // Fetch all data
    if (id) {
      fetchEvent();
      fetchEducationOptions();
      fetchExamOptions();
      fetchCountries();
    }
  }, [id]);

  const scrollToForm = () => {
    const form = document.getElementById("registration-form");
    if (form) {
      form.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (submitLoading) return;

    setSubmitLoading(true);
    try {
      const formDataPayload = {
        firstName: formData.first_name,
        lastName: formData.last_name,
        phone: formData.mobile_no,
        email: formData.email_id,
        education: educationOptions.find((o) => o.educationlevel_id === formData.educationlevel_id)?.educationlevel,
        englishExam: examOptions.find((o) => o.exam_id === formData.exam_id)?.exam_name,
        country: countryOptions.find((o) => o.country_id === formData.country_id)?.country,
        venue: formData.venue,
      };

      console.log("FINAL FORM PAYLOAD:", formDataPayload);

      const registerResponse = await fetch("/api/register-event", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formDataPayload)
      });

      let registerResponseBody = null;
      try {
        registerResponseBody = await registerResponse.clone().json();
      } catch (parseError) {
        registerResponseBody = null;
      }

      const isRegistrationSuccess = Boolean(
        registerResponse &&
          (
            registerResponse.ok ||
            registerResponse.status === 200 ||
            registerResponseBody?.success === true ||
            registerResponseBody?.status === "success"
          )
      );

      if (!isRegistrationSuccess) {
        throw new Error("Event registration failed");
      }

      console.log("🔥 EVENT REGISTRATION SUCCESS");
      console.log("🔥 CONVERSION CODE EXECUTED");
      if (typeof window.gtag === "function") {
        setTimeout(() => {
          window.gtag("event", "conversion", {
            send_to: "AW-759404552/W2BLCO7Z1pYcEIiwjuoC",
          });
        }, 300);
      }

      alert("Registered successfully");
      setFormData({
        first_name: "",
        last_name: "",
        mobile_no: "",
        email_id: "",
        educationlevel_id: "",
        exam_id: "",
        country_id: "",
        venue: "",
        source: source,
        source_remark: sourceRemark,
        branch: branch,
      });
    } catch (error) {
      console.error("Fetch failed:", error);
      alert("Registration failed. Please try again.");
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800">
            {error || "Event not found"}
          </h2>
          <button
            onClick={() => navigate("/events")}
            className="mt-4 inline-flex items-center text-blue-600 hover:text-blue-800"
          >
            <svg
              className="w-5 h-5 mr-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to Events
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Redesigned Hero Section */}
      <div className="relative bg-gradient-to-br from-blue-900 via-indigo-900 to-blue-800 py-12 md:py-20 overflow-hidden">
        {/* Decorative Background Elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10 pointer-events-none">
          <div className="absolute top-0 right-0 transform translate-x-1/2 -translate-y-1/4 w-[500px] h-[500px] bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 transform -translate-x-1/3 translate-y-1/4 w-[400px] h-[400px] bg-white rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <button
            onClick={() => navigate("/events")}
            className="inline-flex items-center text-blue-100 hover:text-white mb-8 transition-colors group"
          >
            <ChevronLeft size={20} className="mr-1 transform group-hover:-translate-x-1 transition-transform" />
            Back to Events
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Column: Content */}
            <div className="text-white">
              <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full border border-white/20 mb-6">
                <img src={logo} alt="Logo" className="w-6 h-6 object-contain" />
                <span className="text-sm font-semibold tracking-wide uppercase">Official Event</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 leading-tight tracking-tight">
                {event.title}
              </h1>
              
              {(event.organizer || event.presentedBy || "Presented by M.S. Education Consultant") && (
                <p className="text-xl md:text-2xl text-blue-100 mb-6 font-medium flex items-center">
                  <span className="w-8 h-[2px] bg-red-500 mr-3"></span>
                  {event.organizer || event.presentedBy || "Presented by M.S. Education Consultant"}
                </p>
              )}
              
              {event.description && (
                <p className="text-lg md:text-xl text-blue-100/80 mb-8 leading-relaxed max-w-xl line-clamp-3">
                  {event.description.replace(/<[^>]*>?/gm, '').substring(0, 165)}...
                </p>
              )}

              {/* Dynamic Date & Time Display in Hero */}
              <div className="flex flex-wrap items-center gap-4 mb-10 text-blue-100 font-medium">
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2.5 rounded-xl border border-white/10">
                  <Clock size={18} className="text-red-400" />
                  <span>{formatDateRange(event.locations)}</span>
                </div>
                {event.locations?.[0]?.time && (
                  <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2.5 rounded-xl border border-white/10">
                    <Clock size={12} className="text-red-400" />
                    <span className="text-sm">{event.locations[0].time}</span>
                  </div>
                )}
              </div>
              
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={scrollToForm}
                  className="bg-red-500 hover:bg-red-600 text-white px-8 py-4 rounded-xl font-bold text-lg shadow-xl shadow-red-500/20 transition-all transform hover:-translate-y-1 active:scale-95"
                >
                  Register Now
                </button>
                <button
                  onClick={() => document.getElementById('about-event').scrollIntoView({ behavior: 'smooth' })}
                  className="bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-xl font-bold text-lg backdrop-blur-sm transition-all border border-white/20 hover:border-white/40"
                >
                  View Details
                </button>
              </div>
            </div>

            {/* Right Column: Poster */}
            <div className="relative group perspective-1000">
              <div className="bg-white p-3 rounded-[2rem] shadow-2xl transform transition-all duration-700 group-hover:scale-[1.02] group-hover:-rotate-1 relative z-20 flex items-center justify-center overflow-hidden border border-white/20">
                <img
                  src={constants.serverURL + event.imgUrl || EventImage}
                  alt={event.title}
                  className="w-full h-auto object-contain rounded-2xl transition-transform duration-700 group-hover:scale-[1.01]"
                />
              </div>
              
              {/* Decorative Glows */}
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-red-500/20 rounded-full blur-[80px] group-hover:bg-red-500/30 transition-colors"></div>
              <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-blue-500/20 rounded-full blur-[80px] group-hover:bg-blue-500/30 transition-colors"></div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-8" id="about-event">
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 md:p-10">
                <div className="space-y-12">
                  {/* About Section: Fully Dynamic from API */}
                  <div>
                    <div className="flex items-center space-x-3 mb-8">
                      <div className="p-2.5 bg-blue-50 rounded-xl text-blue-600 border border-blue-100">
                        <Target size={22} />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">About the Event</h3>
                    </div>

                    {event.description && (
                      <div className="prose prose-blue max-w-none text-gray-600 leading-relaxed">
                        {(() => {
                          const desc = event.description;
                          const containsHTML = /<[a-z][\s\S]*>/i.test(desc) || desc.includes('&nbsp;');
                          
                          if (containsHTML) {
                            return <div dangerouslySetInnerHTML={{ __html: desc }} />;
                          }

                          // Plain text processing
                          return desc.split(/\n\s*\n/).filter(p => p.trim()).map((p, index) => {
                            if (p.includes('\n-') || p.includes('\n*') || p.trim().startsWith('- ') || p.trim().startsWith('* ')) {
                              const lines = p.split('\n').filter(l => l.trim());
                              return (
                                <ul key={index} className="list-disc ml-6 space-y-2 mb-4">
                                  {lines.map((line, i) => (
                                    <li key={i}>{line.replace(/^[-*]\s*/, '')}</li>
                                  ))}
                                </ul>
                              );
                            }
                            return <p key={index} className="mb-4 whitespace-pre-line">{p}</p>;
                          });
                        })()}
                      </div>
                    )}
                  </div>

                {/* Dynamic Event Highlights Cards */}
                {(() => {
                  const highlights = event.highlights || event.topics || [];
                  if (highlights.length === 0) return null;

                  return (
                    <div className="pt-12 border-t border-gray-100">
                      <h3 className="text-2xl font-bold text-gray-900 mb-8">
                        Event Highlights
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {highlights.map((item, index) => (
                          <div key={index} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 transition-all duration-300 hover:shadow-md hover:-translate-y-1 group">
                            <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600 mb-4 group-hover:bg-blue-600 group-hover:text-white transition-colors duration-300">
                              <Star size={24} />
                            </div>
                            <h4 className="text-lg font-bold text-gray-900 leading-tight">
                              {item}
                            </h4>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Improved Event Locations UI */}
                {event.locations && event.locations.length > 0 && (
                  <div className="pt-12 border-t border-gray-100">
                    <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                      <MapPin className="text-red-500 mr-3" size={24} />
                      Event Locations
                    </h3>
                    <div className="flex flex-wrap gap-3 mb-8">
                      {event.locations.map((loc, index) => (
                        <button
                          key={index}
                          onClick={() => setActiveLocation(index)}
                          className={`px-6 py-3 rounded-xl text-sm font-bold transition-all duration-300 flex items-center space-x-2 ${
                            activeLocation === index
                              ? "bg-blue-600 text-white shadow-lg shadow-blue-600/30 scale-105"
                              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                          }`}
                        >
                          <MapPin size={16} />
                          <span>{loc.city}</span>
                        </button>
                      ))}
                    </div>
                    
                    <div className="relative">
                      {event.locations.map((loc, index) => (
                        <div 
                          key={index}
                          className={`transition-all duration-500 ${
                            activeLocation === index 
                              ? "opacity-100 translate-y-0 relative z-10" 
                              : "opacity-0 translate-y-4 absolute inset-0 z-0 pointer-events-none"
                          }`}
                        >
                          {activeLocation === index && (
                            <div className="bg-blue-50/40 rounded-3xl p-8 border border-blue-100/50 backdrop-blur-sm">
                              <h4 className="text-xl font-bold text-gray-900 mb-2">{loc.venue}</h4>
                              
                              {/* Specific Date & Time for this location */}
                              <div className="flex flex-wrap gap-3 mb-5">
                                <div className="flex items-center text-blue-800 text-xs font-bold bg-blue-100/50 px-3 py-1.5 rounded-lg border border-blue-200">
                                  <Clock size={14} className="mr-2 text-blue-600" />
                                  {new Date(loc.date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                                </div>
                                {loc.time && (
                                  <div className="flex items-center text-blue-800 text-xs font-bold bg-blue-100/50 px-3 py-1.5 rounded-lg border border-blue-200">
                                    <Clock size={14} className="mr-2 text-blue-600" />
                                    {loc.time}
                                  </div>
                                )}
                              </div>

                              <p className="text-gray-600 mb-6 flex items-start">
                                <span className="mr-2 mt-1">•</span>
                                {loc.address}
                              </p>
                              {loc.map_url && (
                                <a
                                  href={loc.map_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center text-blue-600 font-bold hover:text-blue-700 transition-colors group"
                                >
                                  View on Google Maps
                                  <svg className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                  </svg>
                                </a>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          </div>

            {/* Related Events - Show if available */}
            {event.relatedEvents && event.relatedEvents.length > 0 && (
              <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 md:p-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Related Events
                </h2>
                <div className="space-y-4">
                  {event.relatedEvents.map((relatedEvent) => (
                    <div
                      key={relatedEvent.id}
                      className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-blue-50 transition cursor-pointer"
                      onClick={() => navigate(`/events/${relatedEvent.id}`)}
                    >
                      <div className="bg-blue-100 rounded-full p-3 mr-4">
                        <svg
                          className="w-6 h-6 text-blue-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">
                          {relatedEvent.title}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {relatedEvent.date}
                        </p>
                      </div>
                      <svg
                        className="w-5 h-5 text-gray-400 ml-auto"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="lg:col-span-1" id="registration-form">
            <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden sticky top-8 mt-12 lg:mt-0 transition-all duration-300">
              {/* Form Header with Trust/Urgency Signals */}
              <div className="bg-gradient-to-r from-blue-700 via-indigo-800 to-blue-900 px-8 py-7 text-center">
                <h2 className="text-2xl font-bold text-white mb-3 tracking-tight">Reserve Your Spot</h2>
                <div className="flex items-center justify-center flex-wrap gap-2 text-blue-100/90 text-[10px] md:text-xs font-bold uppercase tracking-widest">
                  <span className="flex items-center"><span className="w-1 h-1 bg-red-400 rounded-full mr-1.5 animate-pulse"></span>Free Entry</span>
                  <span className="w-1 h-1 bg-white/20 rounded-full hidden sm:block"></span>
                  <span>Limited Seats</span>
                  <span className="w-1 h-1 bg-white/20 rounded-full hidden sm:block"></span>
                  <span>Instant Confirmation</span>
                </div>
              </div>

              <div className="p-8 md:p-10">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-5">
                    <div className="space-y-1.5">
                      <label className="text-sm font-bold text-gray-700 ml-1">First Name*</label>
                      <input
                        type="text"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        placeholder="John"
                        required
                        className="w-full border-gray-200 focus:border-blue-500 focus:ring-blue-500 p-3.5 rounded-xl transition-all shadow-sm bg-gray-50/50"
                      />
                    </div>
                    
                    <div className="space-y-1.5">
                      <label className="text-sm font-bold text-gray-700 ml-1">Last Name*</label>
                      <input
                        type="text"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        placeholder="Doe"
                        required
                        className="w-full border-gray-200 focus:border-blue-500 focus:ring-blue-500 p-3.5 rounded-xl transition-all shadow-sm bg-gray-50/50"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-sm font-bold text-gray-700 ml-1">Mobile No*</label>
                      <input
                        type="tel"
                        name="mobile_no"
                        value={formData.mobile_no}
                        onChange={handleChange}
                        placeholder="+91 9876543210"
                        required
                        className="w-full border-gray-200 focus:border-blue-500 focus:ring-blue-500 p-3.5 rounded-xl transition-all shadow-sm bg-gray-50/50"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-sm font-bold text-gray-700 ml-1">E-Mail Id*</label>
                      <input
                        type="email"
                        name="email_id"
                        value={formData.email_id}
                        onChange={handleChange}
                        placeholder="john.doe@example.com"
                        required
                        className="w-full border-gray-200 focus:border-blue-500 focus:ring-blue-500 p-3.5 rounded-xl transition-all shadow-sm bg-gray-50/50"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-sm font-bold text-gray-700 ml-1">Last Education*</label>
                      <select
                        name="educationlevel_id"
                        value={formData.educationlevel_id}
                        onChange={handleChange}
                        required
                        className="w-full border-gray-200 focus:border-blue-500 focus:ring-blue-500 p-3.5 rounded-xl transition-all shadow-sm bg-gray-50/50 appearance-none bg-no-repeat bg-[right_1rem_center]"
                      >
                        <option value="">Select your option</option>
                        {educationOptions.map((option) => (
                          <option
                            key={option.educationlevel_id}
                            value={option.educationlevel_id}
                          >
                            {option.educationlevel}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-sm font-bold text-gray-700 ml-1">English Exam?</label>
                      <select
                        name="exam_id"
                        value={formData.exam_id}
                        onChange={handleChange}
                        className="w-full border-gray-200 focus:border-blue-500 focus:ring-blue-500 p-3.5 rounded-xl transition-all shadow-sm bg-gray-50/50"
                      >
                        <option value="">Select your option</option>
                        {examOptions.map((option) => (
                          <option key={option.exam_id} value={option.exam_id}>
                            {option.exam_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-sm font-bold text-gray-700 ml-1">Country Preference*</label>
                      <select
                        name="country_id"
                        value={formData.country_id}
                        onChange={handleChange}
                        required
                        className="w-full border-gray-200 focus:border-blue-500 focus:ring-blue-500 p-3.5 rounded-xl transition-all shadow-sm bg-gray-50/50"
                      >
                        <option value="">Select your option</option>
                        {countryOptions.map((option) => (
                          <option
                            key={option.country_id}
                            value={option.country_id}
                          >
                            {option.country}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-sm font-bold text-gray-700 ml-1">Venue*</label>
                      <select
                        name="venue"
                        value={formData.venue}
                        onChange={handleChange}
                        required
                        className="w-full border-gray-200 focus:border-blue-500 focus:ring-blue-500 p-3.5 rounded-xl transition-all shadow-sm bg-gray-50/50"
                      >
                        <option value="">Select your venue</option>
                        <option value="Ahmedabad">Ahmedabad</option>
                        <option value="Anand">Anand</option>
                        <option value="Navsari">Navsari</option>
                        <option value="Surat">Surat</option>
                        <option value="Online-Webinar">Online-Webinar</option>
                      </select>
                    </div>

                    {/* Hidden fields for source, source_remark, and branch */}
                    <input
                      type="hidden"
                      name="source"
                      value={formData.source}
                    />
                    <input
                      type="hidden"
                      name="source_remark"
                      value={formData.source_remark}
                    />
                    <input
                      type="hidden"
                      name="branch"
                      value={formData.branch}
                    />
                  </div>

                  <div className="pt-4">
                    <button
                      type="submit"
                      disabled={submitLoading}
                      className="w-full bg-red-600 hover:bg-red-700 text-white py-4 rounded-xl font-bold text-lg shadow-xl shadow-red-600/20 transition-all transform hover:-translate-y-0.5 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed group"
                    >
                      {!submitLoading && (
                        <span className="flex items-center justify-center">
                          Reserve My Spot
                          <svg className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                        </span>
                      )}
                      {submitLoading && (
                        <div className="flex justify-center items-center w-full">
                          <div role="status">
                            <svg
                              aria-hidden="true"
                              className="w-8 h-8 text-white/20 animate-spin fill-white"
                              viewBox="0 0 100 101"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                fill="currentColor"
                              />
                              <path
                                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                fill="currentFill"
                              />
                            </svg>
                          </div>
                        </div>
                      )}
                    </button>
                    <p className="text-center text-gray-500 text-[11px] mt-4 flex items-center justify-center font-medium">
                      <Clock size={14} className="mr-1.5 text-blue-600" />
                      Our specialized team will contact you within 24 hours
                    </p>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      <RegisterCTA />
    </div>
  );
}

function RegisterCTA() {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    // Function to check if form is visible in viewport
    const checkFormVisibility = () => {
      const form = document.getElementById("registration-form");
      if (form) {
        const rect = form.getBoundingClientRect();
        // Hide button if form is in viewport
        const isInViewport =
          rect.top <=
            (window.innerHeight || document.documentElement.clientHeight) &&
          rect.bottom >= 0;
        setIsVisible(!isInViewport);
      }
    };

    // Check visibility on mount
    checkFormVisibility();

    // Set up scroll listener
    window.addEventListener("scroll", checkFormVisibility);

    // Clean up
    return () => {
      window.removeEventListener("scroll", checkFormVisibility);
    };
  }, []);

  const scrollToForm = () => {
    const form = document.getElementById("registration-form");
    if (form) {
      form.scrollIntoView({ behavior: "smooth" });
    }
  };

  // If not visible, don't render anything
  if (!isVisible) return null;

  return (
    <button
      onClick={scrollToForm}
      className="fixed lg:hidden left-10 max-md:left-5 bottom-10 bg-blue-600 text-white p-3 rounded-lg shadow-lg hover:bg-blue-700 transition-all duration-300 ease-in-out group z-50"
      aria-label="Register Now"
    >
      <div className="flex items-center">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6 mr-2"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5l7 7-7 7"
          />
        </svg>
        <span className="inline-block whitespace-nowrap overflow-hidden">
          Register For Free
        </span>
      </div>
    </button>
  );
}
